/**
 * Hook for extracting and processing assessment data
 * Dynamic skill extraction with zero hardcoded skill names
 */

import { CandidateAssessmentSummary } from '../types/job.types';
import type { TFunction } from 'i18next';
import { resolveInferredExplanation } from '../utils/assessment/inferred-explanation';

/** Title-case a skill name: "self-direction" → "Self-Direction", "ownership" → "Ownership" */
const titleCase = (s: string): string =>
  s.replace(/\b\w/g, c => c.toUpperCase());

export interface SkillData {
  name: string;
  value: number;
}

export interface SkillDetail {
  name: string;
  level: number;
  explanation: string;
  maxLevel: number;
}

export interface SkillTooltipData {
  title: string;
  level: number | undefined;
  explanation: string;
}

/**
 * Get skills data for a specific section
 * Fully dynamic - reads skill names from sections data
 */
export const getSkillsData = (
  assessment: CandidateAssessmentSummary | null | undefined,
  section: string
): SkillData[] => {
  if (!assessment) return [];

  if (assessment.sections && Array.isArray(assessment.sections)) {
    const sectionData = assessment.sections.find(s => s.section === section);
    if (sectionData && sectionData.skill_scores) {
      const coreStrength = sectionData.skill_scores.find(s => s.skill_area.includes('Core Concepts Strengths'));
      const coreWeakness = sectionData.skill_scores.find(s => s.skill_area.includes('Core Concepts Weaknesses'));
      const otherSkills = sectionData.skill_scores.filter(
        s => !s.skill_area.includes('Core Concepts Strengths') && !s.skill_area.includes('Core Concepts Weaknesses')
      );

      const result: SkillData[] = [];

      if (coreStrength || coreWeakness) {
        const scores = [coreStrength?.average_score, coreWeakness?.average_score].filter((v): v is number => v != null);
        if (scores.length > 0) {
          result.push({
            name: 'Core Concepts',
            value: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
          });
        }
      }

      otherSkills.forEach(skill => {
        result.push({
          name: titleCase(skill.skill_area),
          value: Math.round(skill.average_score),
        });
      });

      return result;
    }
  }

  return [];
};

/**
 * Get skill tooltips for a specific section
 * Groups per-question scores by skill area
 */
export const getSkillTooltips = (
  assessment: CandidateAssessmentSummary | null | undefined,
  section: string,
  tInferred?: TFunction<'candidates'>,
): Record<string, SkillTooltipData[]> => {
  if (!assessment) return {};

  if (assessment.per_question_scores && Array.isArray(assessment.per_question_scores)) {
    const tooltips: Record<string, SkillTooltipData[]> = {};

    assessment.per_question_scores
      .filter(q => q.section === section)
      .forEach(questionScore => {
        const isCoreStrength = questionScore.skill_area.includes('Core Concepts Strengths');
        const isCoreWeakness = questionScore.skill_area.includes('Core Concepts Weaknesses');
        const displayName = (isCoreStrength || isCoreWeakness) ? 'Core Concepts' : titleCase(questionScore.skill_area);

        if (!tooltips[displayName]) {
          tooltips[displayName] = [];
        }

        // When the score is a benefit-of-doubt default (system failed to
        // capture the answer), surface the human-readable explanation
        // instead of the (empty) evidence string. This ensures the
        // tooltip never shows a blank cell, and recruiters always know
        // why a 5/10 was assigned.
        const explanationText = tInferred
          ? resolveInferredExplanation(questionScore, tInferred)
          : questionScore.is_inferred
            ? questionScore.score_explanation ?? questionScore.evidence ?? ''
            : questionScore.evidence;

        tooltips[displayName].push({
          title: titleCase(questionScore.skill_area),
          level: questionScore.score,
          explanation: explanationText
        });
      });

    // Sections sourced entirely from the spider-chart assembler may
    // have skills with no per_question_scores entries (the placeholder
    // QuestionScore sits inside skill_scores[i].question_scores, not in
    // the flat per_question_scores array). Surface those too so every
    // axis on the chart has a tooltip.
    if (assessment.sections && Array.isArray(assessment.sections)) {
      const sectionData = assessment.sections.find(s => s.section === section);
      if (sectionData?.skill_scores) {
        sectionData.skill_scores.forEach(skill => {
          const isCoreStrength = skill.skill_area.includes('Core Concepts Strengths');
          const isCoreWeakness = skill.skill_area.includes('Core Concepts Weaknesses');
          const displayName = (isCoreStrength || isCoreWeakness) ? 'Core Concepts' : titleCase(skill.skill_area);
          if (tooltips[displayName] && tooltips[displayName].length > 0) return; // already populated from per_question_scores
          if (!skill.question_scores || skill.question_scores.length === 0) return;
          const placeholder = skill.question_scores[0];
          const explanationText = tInferred
            ? resolveInferredExplanation(placeholder, tInferred)
            : placeholder.is_inferred
              ? placeholder.score_explanation ?? placeholder.evidence ?? ''
              : placeholder.evidence ?? '';
          tooltips[displayName] = [{
            title: titleCase(skill.skill_area),
            level: placeholder.score,
            explanation: explanationText,
          }];
        });
      }
    }

    return tooltips;
  }

  return {};
};

/**
 * Get assessment scores (fit, tech, soft)
 * Extracts section averages from sections
 */
export const getAssessmentScores = (assessment: CandidateAssessmentSummary | null | undefined) => {
  const fitScore = assessment?.fit_assessment?.fit_score ?? assessment?.fit_score;

  if (assessment?.sections && Array.isArray(assessment.sections)) {
    const techSection = assessment.sections.find(s => s.section === 'Technical Skills');
    const softSection = assessment.sections.find(s => s.section === 'Soft Skills');

    return {
      fitScore,
      techScore: techSection?.average_score ? Math.round(techSection.average_score * 10) : undefined,
      softScore: softSection?.average_score ? Math.round(softSection.average_score * 10) : undefined,
    };
  }

  return {
    fitScore,
    techScore: undefined,
    softScore: undefined,
  };
};
